import { PortableTextBlock } from '@portabletext/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import clsx from 'clsx';
import { Link } from 'gatsby';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CSSProperties, ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import BtnAccessibility from 'components/BtnAccessibility';
import { ScrollUpButton } from 'components/ScrollUpButton';
import { Arrow } from 'components/icons';

import { PageFeatureFlags } from 'models/flags/page';
import { IQueryParams, ITrackEventProps } from 'typescriptDefs';
import { TAfAdsets } from 'typescriptDefs/afTracking';
import { TNavSettings } from 'typescriptDefs/layout';

import { afAdsetsPages, afChannels } from 'helpers/consts';
import { useIsLDLoaded } from 'helpers/launchDarkly';
import { useScrollDepth, useScrollY, useTrackScrollDepth } from 'helpers/useScrollDepth';
import { usePageView } from 'utils/hooks/use-page-view';
import { RegistrationParamsProvider, TRegistrationParamsContext } from 'utils/hooks/use-registration-params-context';
import { TLocale, defaultLang, getLocaleAndBasePath } from 'utils/i18n';

import { NavFullMinimizedHeight, NavMobileHeight } from 'styles/tw-themes/spaceValues';

import Footer from './Footer';
import { LongBanner } from './LongBanner';
import Navbar from './Navbar';
import { SmartBanner } from './SmartBanner';

const canadaPostBannerBlacklist = ['www page Mural'];

export const LocaleContext = createContext<TLocale>(defaultLang);
const queryClient = new QueryClient();

type Props = {
  afAdset?: TAfAdsets;
  children: ReactNode;
  className?: string;
  containerClassname?: string;
  ctaToNavbar?: string;
  displayFooter?: boolean;
  displayNav?: boolean;
  displaySmartBanner?: boolean;
  footerDisclaimer?: PortableTextBlock | PortableTextBlock[];
  location: any;
  navSettings?: TNavSettings;
  oldTheme?: boolean;
  pageIsMultilingual: boolean;
  pageName: string;
  pageVariant?: string;
  pageViewProperties?: Record<string, unknown>;
  referralCode?: string;
  registrationParamsContext?: TRegistrationParamsContext;
  skipPageView?: boolean;
  trackScrollDepth?: boolean;
  trackScrollDepthProps?: Partial<ITrackEventProps>;
};

export const Layout = ({
  afAdset,
  children,
  className,
  containerClassname,
  ctaToNavbar,
  displayFooter = true,
  displayNav = true,
  displaySmartBanner = true,
  footerDisclaimer,
  location,
  navSettings,
  oldTheme = false,
  pageIsMultilingual,
  pageName,
  pageVariant,
  pageViewProperties,
  referralCode,
  registrationParamsContext = { intent: '', sendUserToApp: false, appOneLink: '', name: '' },
  skipPageView = false,
  trackScrollDepth = true,
  trackScrollDepthProps,
}: Props) => {
  const locale: TLocale = location ? getLocaleAndBasePath(location.pathname).locale : 'en';
  useIsLDLoaded();
  const showCanadaPostBanner: boolean = useFlags()[PageFeatureFlags.ShowCanadaPostBanner];
  const [isOpen, setIsOpen] = useState(true);
  const isCanadaPostBannerVisible =
    showCanadaPostBanner && !navSettings?.banner && isOpen && !canadaPostBannerBlacklist.includes(pageName);

  const queryParams: IQueryParams = {
    hash: referralCode ? { referral: referralCode } : {},
    search: {
      af_channel: afChannels.general,
      af_adset: afAdset ? afAdset : afAdsetsPages[location.pathname],
    },
  };

  const [showBannerBasic, setShowBannerBasic] = useState<boolean>(true);

  usePageView({ pageName, properties: pageViewProperties, skip: skipPageView });

  useEffect(() => {
    if (!displaySmartBanner || bannerBasicLocationBlacklist[location.pathname]) {
      setShowBannerBasic(false);
    }
  }, [displaySmartBanner, location.pathname]);

  const { refMain, scrollDepth } = useTrackPageScrolling({
    locale,
    pageName,
    pageVariant,
    trackScrollDepth,
    trackScrollDepthProps,
  });

  const notificationBanner =
    navSettings?.banner || (isCanadaPostBannerVisible && CanadaPostBanner(() => setIsOpen(false)));

  return (
    <LocaleContext.Provider value={locale || defaultLang}>
      <RegistrationParamsProvider {...registrationParamsContext}>
        <QueryClientProvider client={queryClient}>
          <div
            data-theme="light"
            className={twMerge(
              clsx(
                'flex min-h-[100vh] flex-col overflow-x-hidden font-basel-grotesk text-grey-500',
                containerClassname,
              ),
            )}
          >
            {displayNav && (
              <Navbar
                darkBg={navSettings?.darkBg}
                pageProps={{ locale, location, pageIsMultilingual, pageName, queryParams, variant: pageVariant }}
                ctaToNavbar={ctaToNavbar}
                banner={notificationBanner || undefined}
                navItemsOverride={navSettings?.navItemsOverride}
              />
            )}
            <main
              ref={refMain}
              className={twMerge(clsx('relative z-belowNav mb-auto', className))}
              data-theme={oldTheme ? 'legacy' : 'light'}
            >
              <div
                className={twMerge(
                  clsx(
                    'h-[var(--layout-top-mobile)] bg-white transition-[height] lg:h-[var(--layout-top-desktop)]',
                    navSettings?.darkBg && 'bg-primary-500',
                    navSettings?.navItemsOverride && 'lg:h-navHeight',
                    className,
                  ),
                )}
                style={
                  {
                    '--layout-top-mobile': `${NavMobileHeight + (isCanadaPostBannerVisible ? 100 : notificationBanner ? 60 : 0)}px`,
                    '--layout-top-desktop': `${NavFullMinimizedHeight + (isCanadaPostBannerVisible ? 44 : 0)}px`,
                  } as CSSProperties
                }
              />
              {showBannerBasic && scrollDepth['0.25'] && <SmartBanner queryParams={queryParams} newHomepage={false} />}
              {children}
              <ScrollUpButton />
              <BtnAccessibility />
            </main>
            {displayFooter && <Footer pageDisclaimer={footerDisclaimer} pageName={pageName} location={location} />}
          </div>
        </QueryClientProvider>
      </RegistrationParamsProvider>
    </LocaleContext.Provider>
  );
};

const bannerBasicLocationBlacklist = {
  '/cover/creditline': '/cover/creditline',
  '/cover/creditline/': '/cover/creditline/',
  '/cover/overdraft': '/cover/overdraft',
  '/cover/overdraft/': '/cover/overdraft/',
};

const useTrackPageScrolling = ({
  locale,
  pageName,
  pageVariant,
  trackScrollDepth,
  trackScrollDepthProps,
}: {
  locale: TLocale;
  pageName: string;
  pageVariant?: string;
  trackScrollDepth: boolean;
  trackScrollDepthProps?: Partial<ITrackEventProps>;
}) => {
  const refMain = useRef<HTMLElement>(null);
  const scrollY = useScrollY({ trackScrollDepth });
  const [mainHeight, setMainHeight] = useState<number>(0);

  useEffect(() => {
    if (refMain.current) {
      const refMainClientRect = refMain.current.getBoundingClientRect();
      setMainHeight(refMainClientRect.height);
    }
  }, [refMain, scrollY]);

  const scrollDepth = useScrollDepth({
    elementHeight: mainHeight,
    scrollY,
    trackScrollDepth,
  });

  useTrackScrollDepth({ locale, pageName, pageVariant, scrollDepth, trackScrollDepthProps });

  return {
    refMain,
    scrollDepth,
  };
};

const CanadaPostBanner = (handleClose: () => void) => () => {
  const locale = useContext(LocaleContext);
  return (
    <LongBanner trackName="close-canada-post-banner" handleClose={handleClose}>
      <span className="text-sm lg:mr-24">
        {
          {
            en: 'KOHO virtual cards are Canada Post strike proof.',
            fr: 'Les cartes virtuelles KOHO sont à l’épreuve de la grève de Postes Canada.',
          }[locale]
        }
      </span>
      <Link
        to={`${locale === 'fr' ? '/fr' : ''}/instant-approval-virtual-card`}
        className="flex w-fit items-center justify-center gap-8 text-left text-sm font-medium text-white hover:underline"
      >
        {{ en: 'Get yours instantly', fr: 'Obtenez la vôtre instantanément' }[locale]}
        <Arrow className="h-16 w-16" />
      </Link>
    </LongBanner>
  );
};
